import { Intent } from "@blueprintjs/core";
import {
  CREATE_COMMENT,
  CommentableEntity,
  RichTextDocument,
  UPDATE_COMMENT,
  UserId,
  convertRichTextFromPlainText,
  isRichTextFromEmptyString,
} from "@hex/common";
import {
  PlateProvider,
  createPluginFactory,
  getPlateActions,
  usePlateStore,
} from "@udecode/plate-core";
import { isSelectionInMentionInput } from "@udecode/plate-mention";
import React, {
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";
import { v4 as uuid } from "uuid";

import { HexButton } from "../../hex-components";
import { inputStyles } from "../../hex-components/HexInput";
import { useHexVersionSelector } from "../../hex-version-multiplayer/state-hooks/hexVersionStateHooks";
import { useDebouncedCallback } from "../../hooks/useDebouncedCallback";
import { LocalStorageKeys, useLocalStorage } from "../../hooks/useLocalStorage";
import { CyData } from "../../util/cypress";
import { useHexVersionAOContext } from "../../util/hexVersionAOContext";
import { HotKeys } from "../../util/hotkeys";
import { Keys } from "../../util/Keys";
import { HotkeyHelperTooltip } from "../common/HotkeyHelperTooltip";
import { PlateRichTextEditor } from "../rich-text/PlateRichTextEditor";

export interface CommentEditorProps {
  readonly className?: string;
  // if not provided, this will create a comment
  readonly commentId?: string;
  readonly entityId: string;
  readonly creatorId: UserId;
  readonly entityType: CommentableEntity;
  readonly initialRichText: RichTextDocument | null;
  readonly helperText: string;
  readonly onComplete: () => void;
  readonly appComment: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  width: 100%;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  margin-top: 8px;
`;

const StyledCancelButton = styled(HexButton)`
  margin-right: 5px;
`;

const StyledPlateRichTextEditor = styled(PlateRichTextEditor)`
  ${inputStyles}
  min-height: 65px;
  max-height: 315px;
  padding: 5px 10px;
`;

export const CommentEditor: React.ComponentType<CommentEditorProps> =
  React.memo(function CommentEditor(props: CommentEditorProps) {
    const plateId = props.commentId ?? props.entityId;
    return (
      <PlateProvider id={plateId}>
        <CommentEditorInternal {...props} plateId={plateId} />
      </PlateProvider>
    );
  });

type InternalProps = CommentEditorProps & { plateId: string };

const CommentEditorInternal: React.ComponentType<InternalProps> = React.memo(
  function CommentEditorInternal({
    appComment,
    className,
    commentId,
    creatorId,
    entityId,
    entityType,
    helperText,
    initialRichText,
    onComplete,
    plateId,
  }: InternalProps) {
    const { dispatchAO } = useHexVersionAOContext();
    const hexVersionId = useHexVersionSelector({
      selector: (hexVersion) => hexVersion.id,
    });
    const plateStore = usePlateStore(plateId);

    const [savedCommentValue, setSavedCommentValue] = useLocalStorage(
      LocalStorageKeys.COMMENT_STATE(),
    );

    const resetValue = useCallback(() => {
      // Initialize editor with RichText provided by parent (ie. if editing a comment)
      // Otherwise, check for a saved comment value in local storage
      const newValue =
        initialRichText ??
        savedCommentValue?.comment ??
        convertRichTextFromPlainText("");
      const actions = getPlateActions(plateId);
      actions.resetEditor();
      actions.value(newValue);
    }, [initialRichText, plateId, savedCommentValue]);

    useEffect(() => {
      const commentingOnSameThing =
        savedCommentValue != null &&
        savedCommentValue.entityId === entityId &&
        savedCommentValue.entityType === entityType &&
        savedCommentValue.hexVersionId === hexVersionId;
      if (commentingOnSameThing && savedCommentValue.comment != null) {
        resetValue();
      } else if (!commentingOnSameThing) {
        // Clear saved state
        setSavedCommentValue(null);
      }
    }, [
      entityId,
      entityType,
      hexVersionId,
      plateId,
      resetValue,
      savedCommentValue,
      setSavedCommentValue,
    ]);

    useEffect(() => {
      resetValue();
    }, [resetValue]);

    const getCommentEditorValue = useCallback(() => {
      return plateStore.get.value() as RichTextDocument;
    }, [plateStore.get]);

    const onBlur = useCallback(() => {
      onComplete();
      // Store the partial comment if not editing an existing comment
      if (initialRichText == null) {
        setSavedCommentValue({
          entityId,
          entityType,
          hexVersionId,
          comment: getCommentEditorValue(),
        });
      }
      resetValue();
    }, [
      entityId,
      entityType,
      getCommentEditorValue,
      hexVersionId,
      initialRichText,
      onComplete,
      resetValue,
      setSavedCommentValue,
    ]);

    const onCancel = useCallback(() => {
      onComplete();
      setSavedCommentValue(null);
      resetValue();
    }, [onComplete, resetValue, setSavedCommentValue]);

    const addComment = useCallback(
      (newCommentRichText: RichTextDocument) => {
        setSavedCommentValue(null);
        onComplete();
        if (newCommentRichText.length > 0) {
          if (commentId == null) {
            dispatchAO(
              CREATE_COMMENT.create({
                commentId: uuid(),
                creatorId,
                entityType,
                entityId,
                appComment,
                richText: newCommentRichText,
              }),
            );
          } else {
            dispatchAO(
              UPDATE_COMMENT.create({
                commentId,
                key: "richText",
                value: newCommentRichText,
              }),
            );
          }
        }
      },
      [
        appComment,
        commentId,
        creatorId,
        dispatchAO,
        entityId,
        entityType,
        onComplete,
        setSavedCommentValue,
      ],
    );

    const onButtonClick = useCallback(() => {
      addComment(getCommentEditorValue());
    }, [addComment, getCommentEditorValue]);

    const onEditorKeyDown: KeyboardEventHandler = useCallback(
      (evt) => {
        if (evt.key === Keys.ENTER && evt.metaKey) {
          evt.stopPropagation();
          const richText = getCommentEditorValue();
          if (!isRichTextFromEmptyString(richText)) {
            addComment(richText);
          }
        } else if (evt.key === Keys.ESCAPE) {
          const editor = plateStore.get.editor();
          if (editor != null && !isSelectionInMentionInput(editor)) {
            // pressing esc closes the mention input -- we don't want to
            // close the comment editor when escaping from the mentions input
            onBlur();
          }
        }
      },
      [addComment, getCommentEditorValue, onBlur, plateStore.get],
    );

    const [isCommentTextEmpty, setIsCommentTextEmpty] = useState(true);

    const platePlugins = useMemo(() => {
      const commentEditorKeyDownPlugin = createPluginFactory({
        key: "hex_comment_editor",
        handlers: {
          onKeyDown: () => onEditorKeyDown,
        },
      });
      return {
        plugins: [commentEditorKeyDownPlugin()],
      };
    }, [onEditorKeyDown]);

    const handleCommentBodyChange = useDebouncedCallback((newRichText) => {
      setIsCommentTextEmpty(isRichTextFromEmptyString(newRichText));
    }, 50);

    return (
      <Container
        className={className}
        onBlur={onBlur}
        // eslint-disable-next-line react/jsx-no-bind
        onClick={(e) => e.stopPropagation()}
      >
        <StyledPlateRichTextEditor
          autoFocus={true}
          id={plateId}
          placeholder={`${helperText}...`}
          plugins={platePlugins}
          onChange={handleCommentBodyChange}
        />
        <ButtonsContainer>
          <StyledCancelButton
            minimal={true}
            small={true}
            onClick={onCancel}
            // Prevent the onBlur handler on the Container from swallowing the click event
            // eslint-disable-next-line react/jsx-no-bind
            onMouseDown={(e) => e.preventDefault()}
          >
            Cancel
          </StyledCancelButton>
          <HotkeyHelperTooltip
            combo={HotKeys.ADD_COMMENT}
            disabled={isCommentTextEmpty}
            hotkeyName="Add"
          >
            <HexButton
              data-cy={CyData.ADD_COMMENT_BUTTON}
              disabled={isCommentTextEmpty}
              intent={Intent.PRIMARY}
              small={true}
              onClick={onButtonClick}
              // Prevent the onBlur handler on the Container from swallowing the click event
              // eslint-disable-next-line react/jsx-no-bind
              onMouseDown={(e) => e.preventDefault()}
            >
              {helperText}
            </HexButton>
          </HotkeyHelperTooltip>
        </ButtonsContainer>
      </Container>
    );
  },
);
